/**
 * Which cars a screen shows.
 *
 * The stint chart is one row per car, and a full Fun Cup field is over a
 * hundred of them. Unfiltered, the thing you actually want — is our stop
 * shorter than theirs, is that car a stop behind — is somewhere in a wall of
 * rows nobody can scroll. So the default is the team's own cars, and widening
 * is a click.
 *
 * Defaulting to a subset is normally a bad idea, because hiding data silently
 * is how people come to distrust a screen. It is defensible here only because
 * the control says what it is doing — "3 of 108" — and the whole field is one
 * click away. Anything less visible than that should show everything.
 *
 * A filter is a view preference, not a property of the dashboard: two people
 * watching one screen want different rows, and neither should change the
 * other's. It is stored per browser, which is also why this module knows
 * nothing about storage — it takes a value and returns a value.
 */

export interface CarFilter {
  /** null means every class. */
  klass: string | null
  /** Empty means every car that passes the class filter. */
  cars: string[]
}

export const everyCar: CarFilter = { klass: null, cars: [] }

/** One car, as the filter controls need to see it. */
export interface CarInfo {
  number: string
  klass?: string
  /** Whether one of this organisation's entries carries this number. */
  ours: boolean
}

/**
 * Assembles the car list a filter runs over.
 *
 * Numbers come from whatever is being filtered — the stints, not the standings
 * — because a car that has retired keeps its stints and drops off the timing,
 * and disappearing from the chart the moment it stops is the opposite of what
 * a stint chart is for. The class is looked up where it is known and simply
 * absent where it is not.
 */
export function carsInView(
  numbers: readonly string[],
  classOf: (number: string) => string | undefined,
  ourNumbers: readonly string[],
): CarInfo[] {
  const ours = new Set(ourNumbers)
  return [...new Set(numbers)]
    .map((number) => ({ number, klass: classOf(number), ours: ours.has(number) }))
    .sort((a, b) => byCarNumber(a.number, b.number))
}

/** The classes present, in the order a dropdown should list them. */
export function classesInView(cars: readonly CarInfo[]): string[] {
  return [...new Set(cars.map((c) => c.klass).filter((k): k is string => !!k))].sort()
}

/**
 * The cars a filter admits.
 *
 * Class and cars intersect — the class narrows, the car list refines within it,
 * which is the order the controls read in. Keeping them independent instead
 * would mean a car selected outside the chosen class either vanishing with no
 * explanation or overriding the class silently, and both are worse than a
 * selection that visibly prunes.
 */
export function applyCarFilter(cars: readonly CarInfo[], filter: CarFilter): CarInfo[] {
  const chosen = new Set(filter.cars)
  return cars.filter((c) => {
    if (filter.klass && c.klass !== filter.klass) return false
    if (chosen.size > 0 && !chosen.has(c.number)) return false
    return true
  })
}

/**
 * Drops car choices a new class filter has made unreachable.
 *
 * Called when the class changes, so the selection on screen is always the
 * selection in force. Leaving stale numbers in it would show a multi-select
 * listing cars that are not on the chart.
 */
export function pruneToClass(cars: readonly CarInfo[], filter: CarFilter): CarFilter {
  if (!filter.klass || filter.cars.length === 0) return filter
  const inClass = new Set(
    cars.filter((c) => c.klass === filter.klass).map((c) => c.number),
  )
  const kept = filter.cars.filter((n) => inClass.has(n))
  return kept.length === filter.cars.length ? filter : { ...filter, cars: kept }
}

/**
 * What to show before anybody has chosen: the team's own cars, or everything.
 *
 * Only ever applied when there is no stored preference. A team that has
 * deliberately widened to the whole field must not have it narrowed again on
 * the next visit.
 */
export function defaultCarFilter(cars: readonly CarInfo[]): CarFilter {
  const ours = cars.filter((c) => c.ours).map((c) => c.number)
  // Not when it would make no difference: narrowing a six-car field to the
  // three that are ours costs more in confusion than it saves in rows.
  if (ours.length === 0 || cars.length <= 8) return everyCar
  return { klass: null, cars: ours }
}

/** Reads a stored filter back, keeping only what still makes sense. */
export function parseCarFilter(raw: unknown): CarFilter {
  if (!raw || typeof raw !== 'object') return everyCar
  const r = raw as Record<string, unknown>
  return {
    klass: typeof r.klass === 'string' && r.klass ? r.klass : null,
    cars: Array.isArray(r.cars) ? r.cars.filter((c): c is string => typeof c === 'string') : [],
  }
}

/** Whether a filter is hiding anything at all. */
export function filtersAnything(filter: CarFilter): boolean {
  return filter.klass !== null || filter.cars.length > 0
}

/**
 * Sorts car numbers the way a person reads them.
 *
 * Numerically where they are numbers, so 7 comes before 58 and before 436.
 * Lexically they would sort 111 before 7, which looks like a bug every time.
 */
export function byCarNumber(a: string, b: string): number {
  const na = Number(a)
  const nb = Number(b)
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb
  return a.localeCompare(b, undefined, { numeric: true })
}
